import { ref, computed, onMounted, onUnmounted } from 'vue'

export function useInfiniteScroll<T>(
  fetchFn: (page: number, size: number) => Promise<{ list: T[]; total: number }>,
  options: { pageSize?: number; threshold?: number } = {},
) {
  const { pageSize = 20, threshold = 200 } = options

  const items = ref<T[]>([]) as { value: T[] }
  const page = ref(1)
  const total = ref(0)
  const loading = ref(false)
  const hasMore = computed(() => items.value.length < total.value)
  const isEmpty = computed(() => !loading.value && items.value.length === 0)

  async function load() {
    if (loading.value || (!hasMore.value && items.value.length > 0)) return
    loading.value = true
    try {
      const res = await fetchFn(page.value, pageSize)
      items.value.push(...res.list)
      total.value = res.total
      page.value++
    } finally {
      loading.value = false
    }
  }

  async function reset() {
    page.value = 1
    total.value = 0
    items.value = []
    await load()
  }

  function handleScroll() {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      load()
    }
  }

  onMounted(() => {
    window.addEventListener('scroll', handleScroll)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
  })

  return { items, loading, hasMore, isEmpty, load, reset }
}
